import { Dayjs } from 'dayjs'
import { ReactNode, useState } from 'react'

import { Button } from '@/shared/components'
import { Popover } from '@mui/material'
import { DateCalendar } from '@mui/x-date-pickers'

import { ButtonProps } from '../Button/Button'

export type ButtonDatePickerProps = {
  value?: Dayjs | null
  onChange?: (date: Dayjs | null) => void
  children?: ReactNode
  disabled?: boolean
  highlightedDates?: Dayjs[]
  slotProps?: {
    button?: Partial<ButtonProps>
    calendar?: Partial<React.ComponentProps<typeof DateCalendar>>
    popover?: Partial<React.ComponentProps<typeof Popover>>
  }
}

export const ButtonDatePicker: React.FC<ButtonDatePickerProps> = ({
  value,
  onChange,
  children,
  disabled,
  highlightedDates,
  slotProps,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [internalDate, setInternalDate] = useState<Dayjs | null>(null)

  // Use controlled or uncontrolled date value
  const selectedDate = value !== undefined ? value : internalDate

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleDateSelect = (date: Dayjs | null) => {
    if (value === undefined) {
      setInternalDate(date)
    }

    if (onChange) {
      onChange(date)
    }

    handleClose()
  }

  const isHighlightedDate = (date: Dayjs): boolean => {
    if (!highlightedDates) return false

    return highlightedDates.some(highlightedDate =>
      highlightedDate.format('DD/MM/YYYY') === date.format('DD/MM/YYYY'),
    )
  }

  const open = Boolean(anchorEl)

  return (
    <>
      <Button
        onClick={handleClick}
        disabled={disabled}
        {...slotProps?.button}
      >
        {children}
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        {...slotProps?.popover}
      >
        <DateCalendar
          value={selectedDate}
          onChange={handleDateSelect}
          {...slotProps?.calendar}
          slotProps={{
            ...slotProps?.calendar?.slotProps,
            day: (dayProps) => {
              const isHighlighted = isHighlightedDate(dayProps.day)

              return {
                ...dayProps,
                disabledRipple: true,
                sx: {
                  ...(isHighlighted && {
                    backgroundColor: 'secondary.main',
                    color: 'primary.contrastText',
                    borderRadius: '50%',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                    '&.Mui-selected': {
                      backgroundColor: 'primary.dark',
                    },
                  }),
                },
              }
            },
          }}
        />
      </Popover>
    </>
  )
}
